import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { toast } from 'react-hot-toast';
import { apiService } from '../services/api';
import { AuthState, User, LoginCredentials, RegisterData } from '../types';

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<boolean>;
  register: (data: RegisterData) => Promise<boolean>;
  verifyMFA: (tempToken: string, mfaToken: string, rememberMe?: boolean) => Promise<boolean>;
  logout: () => Promise<void>;
  verifyEmail: (token: string) => Promise<boolean>;
  setupMFA: () => Promise<{ qrCode: string; secret: string } | null>;
  verifyMFASetup: (token: string) => Promise<string[] | null>;
  disableMFA: (mfaToken: string) => Promise<boolean>;
  updateUser: (user: User) => void;
  clearError: () => void;
}

type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_USER'; payload: User | null }
  | { type: 'SET_TOKENS'; payload: { token: string; refreshToken: string } }
  | { type: 'SET_MFA_REQUIRED'; payload: { requiresMFA: boolean; tempToken?: string } }
  | { type: 'LOGOUT' }
  | { type: 'CLEAR_ERROR' };

const initialState: AuthState = {
  user: null,
  token: localStorage.getItem('token'),
  refreshToken: localStorage.getItem('refreshToken'),
  isAuthenticated: false,
  loading: true,
  error: null,
  requiresMFA: false,
  tempToken: null,
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    
    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload,
        loading: false,
        error: null,
      };
    
    case 'SET_TOKENS':
      return {
        ...state,
        token: action.payload.token,
        refreshToken: action.payload.refreshToken,
      };
    
    case 'SET_MFA_REQUIRED':
      return {
        ...state,
        requiresMFA: action.payload.requiresMFA,
        tempToken: action.payload.tempToken || null,
        loading: false,
      };
    
    case 'LOGOUT':
      return {
        ...initialState,
        loading: false,
        token: null,
        refreshToken: null,
      };
    
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    
    default:
      return state;
  }
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Initialize auth state on app load
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (token && storedUser) {
        try {
          // Verify token is still valid by fetching current user
          const response = await apiService.getCurrentUser();
          if (response.success && response.data) {
            dispatch({ type: 'SET_USER', payload: response.data.user });
            localStorage.setItem('user', JSON.stringify(response.data.user));
          } else {
            // Token is invalid, clear storage
            clearAuthData();
            dispatch({ type: 'LOGOUT' });
          }
        } catch (error) {
          // Token is invalid or expired
          clearAuthData();
          dispatch({ type: 'LOGOUT' });
        }
      } else {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    initializeAuth();
  }, []);

  const clearAuthData = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  };

  const login = async (credentials: LoginCredentials): Promise<boolean> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      const response = await apiService.login(credentials);

      if (response.success) {
        if (response.requiresMFA) {
          dispatch({
            type: 'SET_MFA_REQUIRED',
            payload: { requiresMFA: true, tempToken: response.tempToken },
          });
          return false; // MFA required, not fully logged in yet
        } else if (response.data) {
          // Store tokens
          localStorage.setItem('token', response.data.token);
          localStorage.setItem('refreshToken', response.data.refreshToken);
          localStorage.setItem('user', JSON.stringify(response.data.user));

          dispatch({ type: 'SET_TOKENS', payload: response.data });
          dispatch({ type: 'SET_USER', payload: response.data.user });

          toast.success('Login successful!');
          return true;
        }
      }

      dispatch({ type: 'SET_ERROR', payload: response.message });
      return false;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Login failed';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      return false;
    }
  };

  const register = async (data: RegisterData): Promise<boolean> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      const response = await apiService.register(data);

      if (response.success) {
        toast.success('Registration successful! Please check your email for verification.');
        dispatch({ type: 'SET_LOADING', payload: false });
        return true;
      }

      dispatch({ type: 'SET_ERROR', payload: response.message });
      return false;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Registration failed';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      return false;
    }
  };

  const verifyMFA = async (tempToken: string, mfaToken: string, rememberMe?: boolean): Promise<boolean> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      const response = await apiService.verifyMFA(tempToken, mfaToken, rememberMe);

      if (response.success && response.data) {
        // Store tokens
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('refreshToken', response.data.refreshToken);
        localStorage.setItem('user', JSON.stringify(response.data.user));

        dispatch({ type: 'SET_TOKENS', payload: response.data });
        dispatch({ type: 'SET_USER', payload: response.data.user });
        dispatch({ type: 'SET_MFA_REQUIRED', payload: { requiresMFA: false } });

        toast.success('Login successful!');
        return true;
      }

      dispatch({ type: 'SET_ERROR', payload: response.message });
      return false;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'MFA verification failed';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      return false;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await apiService.logout();
    } catch (error) {
      // Ignore logout errors
    } finally {
      clearAuthData();
      dispatch({ type: 'LOGOUT' });
      toast.success('Logged out successfully');
    }
  };

  const verifyEmail = async (token: string): Promise<boolean> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      const response = await apiService.verifyEmail(token);

      if (response.success) {
        toast.success('Email verified successfully!');
        dispatch({ type: 'SET_LOADING', payload: false });
        return true;
      }

      dispatch({ type: 'SET_ERROR', payload: response.message });
      return false;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Email verification failed';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      return false;
    }
  };

  const setupMFA = async (): Promise<{ qrCode: string; secret: string } | null> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      const response = await apiService.setupMFA();

      if (response.success && response.data) {
        dispatch({ type: 'SET_LOADING', payload: false });
        return {
          qrCode: response.data.qrCode,
          secret: response.data.secret,
        };
      }

      dispatch({ type: 'SET_ERROR', payload: response.message });
      return null;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'MFA setup failed';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      return null;
    }
  };

  const verifyMFASetup = async (token: string): Promise<string[] | null> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      const response = await apiService.verifyMFASetup(token);

      if (response.success && response.data) {
        // Update user to reflect MFA is now enabled
        if (state.user) {
          const updatedUser = { ...state.user, mfaEnabled: true };
          dispatch({ type: 'SET_USER', payload: updatedUser });
          localStorage.setItem('user', JSON.stringify(updatedUser));
        }

        toast.success('MFA enabled successfully!');
        dispatch({ type: 'SET_LOADING', payload: false });
        return response.data.backupCodes;
      }

      dispatch({ type: 'SET_ERROR', payload: response.message });
      return null;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'MFA verification failed';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      return null;
    }
  };

  const disableMFA = async (mfaToken: string): Promise<boolean> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      const response = await apiService.disableMFA(mfaToken);

      if (response.success) {
        // Update user to reflect MFA is now disabled
        if (state.user) {
          const updatedUser = { ...state.user, mfaEnabled: false };
          dispatch({ type: 'SET_USER', payload: updatedUser });
          localStorage.setItem('user', JSON.stringify(updatedUser));
        }

        toast.success('MFA disabled successfully!');
        dispatch({ type: 'SET_LOADING', payload: false });
        return true;
      }

      dispatch({ type: 'SET_ERROR', payload: response.message });
      return false;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to disable MFA';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      return false;
    }
  };

  const updateUser = (user: User) => {
    dispatch({ type: 'SET_USER', payload: user });
    localStorage.setItem('user', JSON.stringify(user));
  };

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const value: AuthContextType = {
    ...state,
    login,
    register,
    verifyMFA,
    logout,
    verifyEmail,
    setupMFA,
    verifyMFASetup,
    disableMFA,
    updateUser,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
